// src/lib/batchReembed.js
//
// Ticket 35 (Admin: Health). "Retry all" calls the batch tool that ticket 34
// built (supabase/functions/batch-reembed-books, reader #262) in its admin
// mode. Nothing in src called that mode before the Health screen; the
// single-book mode has its own helper, src/lib/reembedBook.js.
//
// WHAT IT SENDS. { dryRun: false, all: true }. The function is "dry run unless
// told otherwise" (`body.dryRun !== false`), so dryRun: false has to be spelled
// out here, or "Retry all" would list the backlog and change nothing while the
// screen said it had worked.
//
// WHAT IT RETURNS. IT NEVER THROWS. The caller gets one of
//   { ok: true,  embedded, skipped, failed, remaining, fixable, done,
//     stoppedBecause, failures: [{ id, reason }] }
//   { ok: false, reason }
//   { ok: false, timedOut: true, reason }   the browser stopped waiting; the batch
//                                           may still be running, or have finished
// The numbers are the function's own: this does not count anything itself.
// `remaining` and `fixable` are null when the function could not count them,
// and null is "unknown", never zero.
//
// THE DEADLINE. The function stops starting new batches after RUN_BUDGET_MS
// (45 s) but finishes the batch it is in, and every embedding inside it can
// wait on OpenAI. The browser's deadline sits over that, so a hung call ends in
// a reported failure instead of a spinner that never stops. It is also under
// the platform's own limit on how long a function may run, so the platform is
// never what ends the wait. scripts/check-deadline-nesting.mjs holds it above
// the function's budget.

import { supabase } from './supabase';

const FUNCTION_NAME = 'batch-reembed-books';

export const BATCH_INVOKE_TIMEOUT_MS = 120_000;

const asCount = v => (Number.isSafeInteger(v) && v >= 0 ? v : null);

// The browser stopped waiting. The request WAS sent and the function may still be
// embedding books, or may have finished, so this is not "did not run". supabase-js
// reports a deadline it set itself as a FunctionsFetchError whose context is the
// AbortError that ended the wait. The elapsed time is a second signal, in case a
// different version words it differently.
function stoppedWaiting(invokeError, startedAt) {
  if (invokeError?.name !== 'FunctionsFetchError') return false;
  return invokeError.context?.name === 'AbortError'
    || Date.now() - startedAt >= BATCH_INVOKE_TIMEOUT_MS - 500;
}

export async function requestBatchReembed() {
  const startedAt = Date.now();
  try {
    const { data, error: invokeError } = await supabase.functions.invoke(FUNCTION_NAME, {
      body: { dryRun: false, all: true },
      timeout: BATCH_INVOKE_TIMEOUT_MS,
    });

    if (invokeError) {
      if (stoppedWaiting(invokeError, startedAt)) {
        console.error('[batchReembed] stopped waiting; the batch may still be running');
        return {
          ok: false,
          timedOut: true,
          reason: `Stopped waiting after ${BATCH_INVOKE_TIMEOUT_MS / 60000} minutes. The batch may still be running or may have finished.`,
        };
      }
      // Any non-2xx arrives as invokeError with data null; the real reason is in
      // the response body it carries (same recovery as reembedBook.js).
      let reason = invokeError.message;
      try {
        const body = await invokeError.context?.json?.();
        // The function's own failures answer { ok: false, reason }, but its catch-all
        // answers { ok: false, error } (batch-reembed-books/index.ts, the last handler).
        const said = body?.reason || body?.error;
        if (said) reason = said;
      } catch {
        // body wasn't JSON or was already consumed -- keep invokeError.message.
      }
      console.error('[batchReembed] batch not run:', reason);
      return { ok: false, reason };
    }

    if (!data?.ok) {
      const reason = data?.reason || data?.error || 'unexpected response';
      console.error('[batchReembed] batch not run:', reason);
      return { ok: false, reason };
    }

    const embedded = asCount(data.embedded);
    const skipped  = asCount(data.skipped);
    const failed   = asCount(data.failed);
    // The three numbers are what the screen reports. If the function did not
    // send them, say so rather than showing zeros that were never measured.
    if (embedded === null || skipped === null || failed === null) {
      console.error('[batchReembed] answer is missing its counts:', data);
      return { ok: false, reason: 'the batch answered without its counts, so what it did is unknown' };
    }

    const failures = Array.isArray(data.decisions)
      ? data.decisions
          .filter(d => d && d.verdict === 'failed')
          .map(d => ({ id: d.id, reason: d.reason || 'no reason given' }))
      : [];

    return {
      ok: true,
      embedded,
      skipped,
      failed,
      remaining: data.remainingCountFailed ? null : asCount(data.remaining),
      fixable: asCount(data.fixable),
      done: data.done === true,
      stoppedBecause: typeof data.stoppedBecause === 'string' ? data.stoppedBecause : '',
      failures,
    };
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    console.error('[batchReembed] batch not run:', reason);
    return { ok: false, reason };
  }
}
